import React, { useEffect, useState, useContext, useRef } from 'react';
import { AppContext } from '../../../context/Context';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';
import { SocketContext } from '../../../context/SocketContext';
import toast from 'react-hot-toast';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material/';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { headerCellSx, dataRowSx } from '../../../theme/tableTokens';
import { fieldHelpSx } from '../../../theme/fieldTokens';
import { formatCash } from '../../../utils/formatCash';
import LimitsPicker, {
  limitsError,
  limitsErrorText,
  groupLabelSx,
  pickClass,
  pickSubStyle,
  fieldSx,
  LIMIT_MODE_CUSTOM,
  LIMIT_MODE_DEFAULT,
} from './LimitsPicker';

const PANEL_SX = {
  px: 3,
  py: 3,
  borderRadius: '14px',
  backgroundColor: '#151d3b',
  border: '1px solid #2a3a6e',
};

const chipSx = (color) => ({
  color,
  border: `1px solid ${color}`,
  backgroundColor: 'transparent',
  fontSize: '16px',
});

const buttonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
};

// Rows holding fields and picks; && beats the skin's 18px cell padding
const editRowSx = {
  '&& td': { paddingTop: '10px', paddingBottom: '10px' },
};

const errorTextSx = {
  color: '#e8895f',
  fontSize: '16px',
  fontWeight: 700,
  textAlign: 'left',
  mt: 1,
};

const hallTagSx = {
  color: '#8fa3d4',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const onlyDigits = (text) => text.replace(/\D/g, '').replace(/^0+/, '');

// Cells, not the row: the skin's zebra rule on tr outranks an sx background
const selectedRowSx = {
  '& td': { backgroundColor: 'rgba(0, 201, 164, 0.12)' },
};

// 'full'/'hybrid' from the editors, or { returnTo: 'registration', ... } for a member not created yet
const readOpener = (raw) =>
  raw && typeof raw === 'object'
    ? raw
    : { returnTo: raw === 'hybrid' ? 'hybrid' : 'full' };

// Registration draft { [bingoName]: limits }: the db has nothing yet, so the table shows it
const applyDraft = (halls, draft, defaults) => {
  if (!draft) return halls;
  return halls.map((h) => {
    const d = draft[h.bingoName];
    if (!d) return h;
    const daily = Number(d.limitDaily);
    const monthly = Number(d.limitMonthly);
    // Same reading as checkAllUserLimits: at or above the standard on both is the standard
    const isCustom = !(
      daily >= defaults.limitDaily && monthly >= defaults.limitMonthly
    );
    return { ...h, isCustom, limitDaily: daily, limitMonthly: monthly };
  });
};

// Start from the current hall; all halls are preselected only when they already agree
const initialState = (halls, hall) => {
  const names = halls.map((h) => h.bingoName);
  const current = halls.find((h) => h.bingoName === hall) || halls[0];
  const allSame = halls.every(
    (h) =>
      h.isCustom === current.isCustom &&
      h.limitDaily === current.limitDaily &&
      h.limitMonthly === current.limitMonthly
  );
  return {
    value: {
      mode: current.isCustom ? LIMIT_MODE_CUSTOM : LIMIT_MODE_DEFAULT,
      limitDaily: String(current.limitDaily),
      limitMonthly: String(current.limitMonthly),
    },
    selected: allSame ? names : [current.bingoName],
  };
};

function ModalEditUserLimits() {
  const { t } = useTranslation();
  const { socket } = useContext(SocketContext);

  const {
    showModalEditUserLimits,
    updateShowModalEditUserLimits,
    updateFocusOnMainScreen,
    userInfo,
    parentInfo,
    operator,
    hall,
    updateIsLoadingScreen,
    updateIsFullUserEditing,
    updateIsHybridUserEditing,
  } = useContext(AppContext);

  const openerRef = useRef(readOpener(showModalEditUserLimits));
  const isDraft = openerRef.current.returnTo === 'registration';

  // Limits belong to the member, not to the ticket in use; a registration has none yet
  const member = isDraft ? null : parentInfo || userInfo;
  const memberRef = useRef(member);
  useEffect(() => {
    memberRef.current = member;
  }, [member]);

  const hallRef = useRef(hall);
  useEffect(() => {
    hallRef.current = hall;
  }, [hall]);

  const [defaults, setDefaults] = useState(null);
  const [halls, setHalls] = useState([]);
  const [value, setValue] = useState(null);
  const [selected, setSelected] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  // 'batch' = one value for the selected halls; 'perHall' = every row its own limit
  const [editMode, setEditMode] = useState('batch');
  const [rowValues, setRowValues] = useState({});

  // Registration only: the halls as the db returned them, and the draft as it stands now
  const baseHallsRef = useRef([]);
  const draftRef = useRef(isDraft ? openerRef.current.draft || null : null);

  // A save from a hand-picked set keeps the modal open for the next group
  const keepOpenRef = useRef(false);
  const isRefreshRef = useRef(false);

  const close = () => {
    updateShowModalEditUserLimits(false);
    updateIsLoadingScreen(false);
    const { returnTo } = openerRef.current;
    // The registration form is still open underneath
    if (returnTo === 'hybrid') {
      updateIsHybridUserEditing(true);
    } else if (returnTo === 'full') {
      updateIsFullUserEditing(true);
    }
  };

  const fetchLimits = () => {
    socket.emit('checkAllUserLimits', {
      userID: memberRef.current ? memberRef.current.id : null,
      bingoName: hallRef.current,
    });
  };

  useEffect(() => {
    const handleCheckAllUserLimits = (data) => {
      updateIsLoadingScreen(false);
      const isRefresh = isRefreshRef.current;
      isRefreshRef.current = false;

      if (!data || !data.halls || data.halls.length === 0) {
        toast.error(t('limits.loadFailed'));
        // After a save the old table is still worth keeping on screen
        if (!isRefresh) close();
        return;
      }

      const shown = applyDraft(data.halls, draftRef.current, data.defaults);
      baseHallsRef.current = data.halls;
      setDefaults(data.defaults);
      setHalls(shown);
      if (isRefresh) return;

      const initial = initialState(shown, hallRef.current);
      setValue(initial.value);
      setSelected(initial.selected);
    };

    const handleUpdateUserLimits = (data) => {
      setIsSaving(false);
      updateIsLoadingScreen(false);
      if (!data || !data.success) {
        toast.error(t('limits.saveFailed'));
        return;
      }
      toast.success(t('limits.saved'));
      // Refresh the limits the till shows for this user
      socket.emit('checkLimitStatus', {
        userInfo: memberRef.current,
        bingoName: hallRef.current,
      });

      if (!keepOpenRef.current) {
        close();
        return;
      }
      setHasSaved(true);
      setSelected([]);
      isRefreshRef.current = true;
      fetchLimits();
    };

    socket.on('checkAllUserLimits', handleCheckAllUserLimits);
    socket.on('updateUserLimits', handleUpdateUserLimits);

    return () => {
      socket.off('checkAllUserLimits', handleCheckAllUserLimits);
      socket.off('updateUserLimits', handleUpdateUserLimits);
    };
  }, [socket]);

  useEffect(() => {
    updateFocusOnMainScreen(false);
    updateIsLoadingScreen(true);
    fetchLimits();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // The loading screen covers the wait
  if (!value) {
    return null;
  }

  const allNames = halls.map((h) => h.bingoName);
  const isOnlyThisHall = selected.length === 1 && selected[0] === hall;
  const isAllHalls = selected.length === allNames.length;

  const toggleHall = (name) =>
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );

  const error = limitsError(value, defaults);

  const isPerHall = editMode === 'perHall';

  const rowOf = (h) =>
    rowValues[h.bingoName] || {
      mode: h.isCustom ? LIMIT_MODE_CUSTOM : LIMIT_MODE_DEFAULT,
      limitDaily: String(h.limitDaily),
      limitMonthly: String(h.limitMonthly),
    };

  // Every row starts from what the table shows now
  const startPerHall = () => {
    const rows = {};
    halls.forEach((h) => {
      rows[h.bingoName] = {
        mode: h.isCustom ? LIMIT_MODE_CUSTOM : LIMIT_MODE_DEFAULT,
        limitDaily: String(h.limitDaily),
        limitMonthly: String(h.limitMonthly),
      };
    });
    setRowValues(rows);
    setEditMode('perHall');
  };

  const setRow = (name, patch) =>
    setRowValues((prev) => ({
      ...prev,
      [name]: { ...prev[name], ...patch },
    }));

  const rowErrors = isPerHall
    ? halls
        .map((h) => ({
          name: h.bingoName,
          error: limitsError(rowOf(h), defaults),
        }))
        .filter((r) => r.error)
    : [];

  // Only rows that differ from the table, so the audit line names real changes
  const changedRows = () =>
    halls
      .filter((h) => {
        const r = rowOf(h);
        const isCustom = r.mode === LIMIT_MODE_CUSTOM;
        if (isCustom !== h.isCustom) return true;
        return (
          isCustom &&
          (Number(r.limitDaily) !== h.limitDaily ||
            Number(r.limitMonthly) !== h.limitMonthly)
        );
      })
      .map((h) => {
        const r = rowOf(h);
        return {
          bingoName: h.bingoName,
          mode: r.mode,
          limitDaily: Number(r.limitDaily),
          limitMonthly: Number(r.limitMonthly),
        };
      });

  const handleSavePerHall = () => {
    if (rowErrors.length > 0) {
      toast.error(
        `${rowErrors[0].name}: ${limitsErrorText(t, rowErrors[0].error)}`
      );
      return;
    }
    const rows = changedRows();

    if (isDraft) {
      const next = { ...(draftRef.current || {}) };
      rows.forEach((r) => {
        if (r.mode === LIMIT_MODE_CUSTOM) {
          next[r.bingoName] = {
            limitDaily: String(r.limitDaily),
            limitMonthly: String(r.limitMonthly),
          };
        } else {
          delete next[r.bingoName];
        }
      });
      draftRef.current = Object.keys(next).length > 0 ? next : null;
      openerRef.current.onSave(draftRef.current);
      close();
      return;
    }

    if (rows.length === 0) {
      close();
      return;
    }
    keepOpenRef.current = false;
    setIsSaving(true);
    updateIsLoadingScreen(true);
    socket.emit('updateUserLimits', {
      action: 'updateUserLimits',
      userID: memberRef.current.id,
      bingoName: hallRef.current,
      operator: operator,
      rows,
    });
  };

  const handleSave = () => {
    if (error) {
      toast.error(limitsErrorText(t, error));
      return;
    }
    if (selected.length === 0) return;

    const chosenHalls = allNames.filter((name) => selected.includes(name));

    const viaShortcut = isOnlyThisHall || isAllHalls;

    // Registration: update the draft; the form saves it once the member has an id
    if (isDraft) {
      const next = { ...(draftRef.current || {}) };
      chosenHalls.forEach((name) => {
        if (value.mode === LIMIT_MODE_CUSTOM) {
          next[name] = {
            limitDaily: value.limitDaily,
            limitMonthly: value.limitMonthly,
          };
        } else {
          delete next[name];
        }
      });
      draftRef.current = Object.keys(next).length > 0 ? next : null;
      openerRef.current.onSave(draftRef.current);

      if (viaShortcut) {
        close();
        return;
      }
      // Same as editing a member: a hand-picked set stays open for the next group
      setHalls(applyDraft(baseHallsRef.current, draftRef.current, defaults));
      setSelected([]);
      setHasSaved(true);
      return;
    }

    keepOpenRef.current = !viaShortcut;
    setIsSaving(true);
    updateIsLoadingScreen(true);
    socket.emit('updateUserLimits', {
      action: 'updateUserLimits',
      userID: memberRef.current.id,
      bingoName: hallRef.current,
      operator: operator,
      // Table order, not tap order
      halls: chosenHalls,
      mode: value.mode,
      limitDaily: Number(value.limitDaily),
      limitMonthly: Number(value.limitMonthly),
    });
  };

  const name = isDraft
    ? openerRef.current.name || ''
    : `${member.firstname || ''} ${member.lastname || ''}`.trim();

  return (
    <div id="react-confirm-alert">
      <div className="react-confirm-alert-overlay">
        <div className="react-confirm-alert">
          <div className="react-confirm-alert-body" style={{ width: '1400px' }}>
            <h1>
              {name ? t('limits.title', { name }) : t('header.userLimits')}
            </h1>
            <p>
              {t('limits.standardLine', {
                daily: formatCash(defaults.limitDaily),
                monthly: formatCash(defaults.limitMonthly),
              })}
            </p>

            <Box sx={{ display: 'flex', gap: 1, maxWidth: '640px', mt: 2 }}>
              <button
                type="button"
                className={pickClass(!isPerHall)}
                onClick={() => setEditMode('batch')}
              >
                {t('limits.modeBatch')}
              </button>
              <button
                type="button"
                className={pickClass(isPerHall)}
                onClick={() => !isPerHall && startPerHall()}
              >
                {t('limits.modePerHall')}
              </button>
            </Box>

            {isPerHall ? (
              // Every hall edited in place, for a player who wants a different limit in each
              <Box sx={{ mt: 2, textAlign: 'left' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>
                        {t('limits.colHall')}
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        {t('limits.modeLabel')}
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        {t('limits.colDaily')}
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        {t('limits.colMonthly')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {halls.map((h) => {
                      const r = rowOf(h);
                      const isCustom = r.mode === LIMIT_MODE_CUSTOM;
                      const rowError = limitsError(r, defaults);
                      const hasError = (field) =>
                        !!rowError &&
                        (rowError.field === field || rowError.field === 'both');
                      return (
                        <TableRow
                          key={h.bingoName}
                          sx={{ ...dataRowSx, ...editRowSx }}
                        >
                          <TableCell>
                            {h.bingoName}
                            {h.bingoName === hall && (
                              <Typography sx={hallTagSx}>
                                {t('limits.thisHall')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ width: '320px' }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <button
                                type="button"
                                className={pickClass(!isCustom)}
                                onClick={() =>
                                  setRow(h.bingoName, {
                                    mode: LIMIT_MODE_DEFAULT,
                                  })
                                }
                              >
                                {t('limits.standard')}
                              </button>
                              <button
                                type="button"
                                className={pickClass(isCustom)}
                                onClick={() =>
                                  setRow(h.bingoName, {
                                    mode: LIMIT_MODE_CUSTOM,
                                  })
                                }
                              >
                                {t('limits.custom')}
                              </button>
                            </Box>
                          </TableCell>
                          {['limitDaily', 'limitMonthly'].map((field) => (
                            <TableCell key={field} sx={{ width: '220px' }}>
                              {isCustom ? (
                                <TextField
                                  autoComplete="off"
                                  value={r[field]}
                                  onChange={(e) =>
                                    setRow(h.bingoName, {
                                      [field]: onlyDigits(e.target.value),
                                    })
                                  }
                                  error={hasError(field)}
                                  inputProps={{
                                    inputMode: 'numeric',
                                    maxLength: 6,
                                  }}
                                  sx={fieldSx}
                                />
                              ) : (
                                <Typography
                                  sx={{
                                    color: '#8fa3d4',
                                    fontSize: '22px',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {formatCash(defaults[field])} kr
                                </Typography>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {rowErrors.length > 0 && (
                  <Typography sx={errorTextSx}>
                    {`${rowErrors[0].name}: ${limitsErrorText(
                      t,
                      rowErrors[0].error
                    )}`}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  mt: 2,
                  alignItems: 'flex-start',
                  textAlign: 'left',
                }}
              >
                {/* What each hall has now; a tap adds it to or drops it from the change */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headerCellSx, width: '56px' }} />
                        <TableCell sx={headerCellSx}>
                          {t('limits.colHall')}
                        </TableCell>
                        <TableCell sx={headerCellSx} align="right">
                          {t('limits.colDaily')}
                        </TableCell>
                        <TableCell sx={headerCellSx} align="right">
                          {t('limits.colMonthly')}
                        </TableCell>
                        <TableCell sx={headerCellSx} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {halls.map((h) => {
                        const isSelected = selected.includes(h.bingoName);
                        return (
                          <TableRow
                            key={h.bingoName}
                            onClick={() => toggleHall(h.bingoName)}
                            sx={{
                              ...dataRowSx,
                              cursor: 'pointer',
                              ...(isSelected && selectedRowSx),
                            }}
                          >
                            <TableCell>
                              {isSelected ? (
                                <CheckBoxIcon
                                  sx={{ fontSize: '30px', color: '#00c9a4' }}
                                />
                              ) : (
                                <CheckBoxOutlineBlankIcon
                                  sx={{ fontSize: '30px', color: '#4a5578' }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {h.bingoName}
                              {h.bingoName === hall && (
                                <Typography sx={hallTagSx}>
                                  {t('limits.thisHall')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {formatCash(h.limitDaily)} kr
                            </TableCell>
                            <TableCell align="right">
                              {formatCash(h.limitMonthly)} kr
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={
                                  h.isCustom
                                    ? t('limits.custom')
                                    : t('limits.standard')
                                }
                                sx={chipSx(h.isCustom ? '#fcad89' : '#8fa3d4')}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>

                <Box
                  sx={{
                    ...PANEL_SX,
                    width: '520px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  <LimitsPicker
                    value={value}
                    onChange={setValue}
                    defaults={defaults}
                  />

                  {/* Shortcuts for the two usual cases; anything else is picked in the table */}
                  <Box>
                    <Typography sx={groupLabelSx}>
                      {t('limits.scopeLabel')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <button
                        type="button"
                        className={pickClass(isOnlyThisHall)}
                        onClick={() => setSelected([hall])}
                      >
                        {t('limits.thisHall')}
                        <span style={pickSubStyle}>{hall}</span>
                      </button>
                      <button
                        type="button"
                        className={pickClass(isAllHalls)}
                        onClick={() => setSelected(allNames)}
                      >
                        {t('limits.scopeAll')}
                        {!isAllHalls && (
                          <span style={pickSubStyle}>
                            {t('limits.selectedOf', {
                              count: selected.length,
                              total: allNames.length,
                            })}
                          </span>
                        )}
                      </button>
                    </Box>
                    <Typography
                      sx={{ ...fieldHelpSx, textAlign: 'left', mb: 0, mt: 1 }}
                    >
                      {t('limits.selectHint')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <div
              className="react-confirm-alert-button-group"
              style={{ marginTop: '24px' }}
            >
              <button
                className="btn-custom-yes"
                onClick={isPerHall ? handleSavePerHall : handleSave}
                disabled={
                  isSaving ||
                  (isPerHall
                    ? rowErrors.length > 0
                    : selected.length === 0 || !!error)
                }
                style={buttonStyle}
              >
                <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                {t('sales.confirm')}
              </button>
              <button
                className="btn-custom-no"
                onClick={close}
                style={buttonStyle}
              >
                <CancelIcon style={{ fontSize: '1.2em' }} />
                {hasSaved ? t('sales.close') : t('sales.cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalEditUserLimits;
